import { useState, useMemo, useCallback, useEffect } from 'react'
import { Chip, Button, Stack, Typography, Select, MenuItem, FormControl } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useIsMobile } from '@/hooks/Responsive'
import BoardList from '@components/Board/BoardList'
import BoardListTable from '@components/Board/BoardListTable'
import BoardListCard from '@components/Board/BoardListCard'
import CancelDialog from './CancelDialog'
import { fetchCommonComboList, fetchMyCourseComboList, fetchMyCourseList, updateCancelState } from '@/api/mypage'
import { useAuth } from '@/components/AuthContext'
import { useConfirm } from '@components/ConfirmProvider'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'

/* 컬럼 정의 */
export function createCoursesTableColumns({ isMobile, onTitleClick, onOpenDialog }) {
  const renderTitle = (v, row) => {
    const statusColor = {
      신청대기:	'secondary2',
      신청취소:	'grey',
      신청반려:	'error',
      신청승인:	'secondary',
      취소대기:	'grey',
      수강취소:	'grey',
      수강중: 'primary',
      수강완료:	'secondary2',
    }[row.signupProgressStateName] || 'grey'

    return (
      <Stack direction={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-start' : 'center'} spacing={isMobile ? 2 : 1}>
        {row.signupProgressStateName && (
          <Chip
            label={row.signupProgressStateName}
            variant="outlined"
            color={statusColor}
          />
        )}
        <Typography
          component="span"
          role="link"
          tabIndex={0}
          variant={isMobile ? 'subtitle3' : 'body1'}
          sx={{
            cursor: 'pointer',
            overflow: isMobile ? 'visible' : 'hidden',
            textOverflow: isMobile ? 'unset' : 'ellipsis',
            whiteSpace: isMobile ? 'normal' : 'nowrap',
            wordBreak: 'keep-all',
            '&:hover': { textDecoration: 'underline', fontWeight: '700' },
          }}
          title={String(v ?? '')}
          onClick={() => onTitleClick?.(row)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onTitleClick?.(row)
          }}
        >
          {v}
        </Typography>
      </Stack>
    )
  }

  const renderSession = (v, row) => {
    return (
      <Typography component="span" color="text.secondary" fontWeight={500}>
        <Typography component="span" color={isMobile ? 'primary.main' : 'text.secondary'} fontWeight={500}>{row?.session ?? 0}</Typography>/{row?.totalSession ?? 0}
      </Typography>
      )
  }

  const renderProgressRate = (v, row) => {
    const session = Number(row?.session) || 0;
    const totalSession = Number(row?.totalSession) || 0;

    const percent =
      totalSession > 0
        ? Math.min(Math.round((session / totalSession) * 100), 100)
        : 0;
                    
    return (
      <Typography color="text.secondary" fontWeight={500}>{row?.session && row?.totalSession ? percent : 0}%</Typography>
      )
  }
  
  const renderCertificate = (v) => {
    if (!v) return '-'

    if (v === '다운로드') {
      return (
        <Button variant="outlined" color="primary" size="table">다운로드</Button>
      )
    }

    if (v === '기간만료') {
      return (
        <Button variant="outlined" color="primary" size="table" disabled>기간만료</Button>
      )
    }
  }

  const renderNote = (v, row) => {
    const showCancelButton = row.signupProgressStateName === '수강중' || row.signupProgressStateName === '신청대기' || row.signupProgressStateName === '신청승인';
    // const showCancelButton = true;
    return (
      showCancelButton && (
        <Button variant="outlined" color="grey" size={isMobile ? 'medium' : 'table'} sx={{ width: isMobile ? '100%' : 'auto', mt: isMobile ? 3 : 0 }} onClick={() => onOpenDialog?.(row)}>수강취소</Button>
      )
    );
  }


  return [
    { field: 'rowNo', headerName: '번호', sortable: false, width: '70px', align: 'center', hideInCard: true },
    { field: 'eduLectureName', headerName: '강좌명', sortable: false, width: 'auto', align: 'left', render: renderTitle, hideTitleInCard: true },
    { field: 'lectureDays', headerName: '수강기간', sortable: false, width: '230px', align: 'center' },
    { field: 'myCourseCnt', headerName: '회차', sortable: false, width: '70px', align: 'center', render: renderSession },
    { field: 'progressRate', headerName: '진도율', sortable: false, width: '70px', align: 'center', render: renderProgressRate },
    { field: 'certificateFileUrl', headerName: '이수증', sortable: false, width: '96px', align: 'center', render: renderCertificate, hideInCard: true },
    { field: 'courseStatus', headerName: '비고', sortable: false, width: '96px', align: 'center', render: renderNote, hideTitleInCard: true },
  ]
}

/* 상단 검색 */
export function CoursesToolbar({ isMobile, status, lecture, course, onStatusChange, onLectureChange, onCourseChange, statusComboData, lectureComboData }) {
  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      alignItems="center"
      spacing={1}
      sx={{
        flex: 'auto',
        width: isMobile ? '100%' : 'auto'
      }}
    >
      {/* 상태 필터 */}
      <FormControl sx={{ flex: 'auto', width: isMobile ? '100%' : '220px', }}>
        <Select
          name="status"
          value={status}
          onChange={(e) => onStatusChange?.(e.target.value)}
          displayEmpty
          MenuProps={{
            PaperProps: {
              sx: {
                width: '100%',
                maxWidth: (theme) => isMobile ? `calc(100vw - ${theme.spacing(8)})` : '220px',
              },
            },
          }}
          sx={{ 
            width: '100%',
            maxWidth: (theme) => isMobile ? `calc(100vw - ${theme.spacing(8)})` : '220px',
          }}
        >
          <MenuItem value="전체">전체</MenuItem>
          {/* <MenuItem value="ENROLLED">수강중</MenuItem>
          <MenuItem value="WAITING">수강대기</MenuItem>
          <MenuItem value="COMPLETED">수강완료</MenuItem>
          <MenuItem value="CANCEL_WAITING">취소대기</MenuItem>
          <MenuItem value="CANCEL">취소</MenuItem> */}
          {statusComboData?.map((item) => (
             <MenuItem
               key={item.code}
               value={item.code}
             >
               {item.codeNm}
             </MenuItem>
           ))}
        </Select>
      </FormControl>
      {/* 새로운 강좌명 필터 (course) */}
      <FormControl sx={{ flex: 'auto', width: isMobile ? '100%' : '320px', }}>
        <Select
          name="course"
          value={course}
          onChange={(e) => onCourseChange?.(e.target.value)}
          displayEmpty
          MenuProps={{
            PaperProps: {
              sx: {
                width: '100%',
                maxWidth: (theme) => isMobile ? `calc(100vw - ${theme.spacing(8)})` : '320px',
              },
            },
          }}
          sx={{ 
            width: '100%',
            maxWidth: (theme) => isMobile ? `calc(100vw - ${theme.spacing(8)})` : '320px',
          }}
        >
          <MenuItem value="전체">전체</MenuItem>
          {lectureComboData?.map((item) => (
             <MenuItem
               key={item.code}
               value={item.code}
             >
               {item.codeNm}
             </MenuItem>
           ))}
        </Select>
      </FormControl>
    </Stack>
  )
}

export default function MyCourses() {
  const { user, loading, isAuthReady } = useAuth();
  const { alert, confirm } = useConfirm();

  const navigate = useNavigate()
  const { isMobile } = useIsMobile()
  const lang = useLang()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedRow(null)
  }

  //취소처리
  const submitDialogCancelProc = async (reason) => {
    try {
      debugger;
        const params = {
          // courseStatus: 'CANCEL',
          eduLectureId: selectedRow.eduLectureId,
          completionStateChgRsn: reason,
          signupProgressStateCode: selectedRow.signupProgressStateCode,
          openYn: selectedRow.openYn,
          accountId: user.accountId
      };
      const response = await updateCancelState(params);
      if (response.success) { // Assuming 200 OK for success
        alert('수강이 취소되었습니다.');
        setRefreshTrigger(prev => prev + 1); // Increment to trigger refresh
        handleCloseDialog(); // Close dialog on success
      } else {
        alert('수강 취소 처리 중 오류가 발생했습니다.'); // Handle non-200 responses
      }
    } catch(error) {
      console.error('수강강좌 취소처리 실패 :', error);
      alert('수강 취소 처리 중 오류가 발생했습니다.'); // Show error to user
    }
  }
  
  const [statusFilter, setStatusFilter] = useState('전체') 
  const [lectureFilter, setLectureFilter] = useState('전체')
  const [courseFilter, setCourseFilter] = useState('전체') // New state for course filter
  const [confirmedStatus, setConfirmedStatus] = useState('전체') 
  const [confirmedLecture, setConfirmedLecture] = useState('전체') 
  const [confirmedCourse, setConfirmedCourse] = useState('전체') // New state for confirmed course filter
  const [confirmedSearch, setConfirmedSearch] = useState('')

  const handleStatusChange = useCallback((value) => {
    setStatusFilter(value)
  }, [])

  const handleLectureChange = useCallback((value) => {
    setLectureFilter(value)
  }, [])

  const handleCourseChange = useCallback((value) => { // New handler for course filter
    setCourseFilter(value)
  }, [])

  const [statusComboData, setStatusComboData] = useState([]);
  const [lectureComboData, setLectureComboData] = useState([]);
  const getMyCourseLectureComboList = async (searchData) => {
    //{ condition, keyword, page: 1 }
    // setLoading(true);
    try {
      const response = await fetchMyCourseComboList(searchData);
      setLectureComboData(response.data);
    } catch (error) {
      console.error('수강강좌 목록 selectbox 불러오기 실패:', error);
    } finally {
      // setLoading(false);
    }
  };
  const getMyCourseStatusComboList = async (searchData) => {
    //{ condition, keyword, page: 1 }
    // setLoading(true);
    try {
      const response = await fetchCommonComboList(searchData);
      setStatusComboData(response.data);
    } catch (error) {
      console.error('수강상태 목록 selectbox 불러오기 실패:', error);
    } finally {
      // setLoading(false);
    }
  };
  useEffect(() => {
    const params = {
        accountId: user.accountId,
        codeEtc: 'SUPGST_PORTAL'//'CPST'
      };
    getMyCourseLectureComboList({params})
    getMyCourseStatusComboList({params})
    
  }, [])



  /* fetcher: 서버에 페이지별 데이터 요청 */
  const fetcher = useCallback(
    async ({ page, pageSize, sort }) => {
      // 서버에 보낼 파라미터를 구성합니다.
      const params = {
        page: page,
        size: pageSize,
        sort: sort ? `${sort.field},${sort.direction}` : 'no,desc',
        //completionStateCode: confirmedStatus !== '전체' ? confirmedStatus : undefined,
        signupProgressStateCode: confirmedStatus !== '전체' ? confirmedStatus : undefined,
        lecture: confirmedLecture !== '전체' ? confirmedLecture : undefined,
        eduLectureId: confirmedCourse !== '전체' ? confirmedCourse : undefined, // Add course filter
        keyword: confirmedSearch || undefined,
        accountId: user.accountId
      };

      // 값이 undefined인 파라미터는 제거합니다.
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      try {
        // NOTE: API 엔드포인트와 파라미터명은 실제 서버 API 명세에 맞게 수정해야 합니다.
        const response = await fetchMyCourseList({ params });
        // return response.data;

        // --- API 호출 시뮬레이션 시작 ---
        // 실제 API가 준비되기 전까지 임시 데이터를 반환합니다.
        const allMockItems = response.data.content;
        
        const total = response.data.totalElements;
        const totalPages = response.data.totalPages;

        return Promise.resolve({ items: allMockItems, total, totalPages });
        // --- API 호출 시뮬레이션 끝 ---

      } catch (error) {
        console.error("Failed to fetch courses:", error);
        // 에러 발생 시 BoardList에 빈 데이터를 반환합니다.
        return { items: [], total: 0 };
      }
    },
    [confirmedStatus, confirmedLecture, confirmedCourse, confirmedSearch, refreshTrigger] 
  )

  const columns = useMemo(
    () =>
      createCoursesTableColumns({
        isMobile,
        onTitleClick: (row) => navigate(buildLangHref(`/mypage/courses/${row.eduLectureId}`, lang)),
        onOpenDialog: (row) => {
          setSelectedRow(row)
          setDialogOpen(true)
        },
      }),
    [isMobile, navigate]
  )

  return (
    <>
      <BoardList
        view={isMobile ? 'card' : 'table'}
        columns={columns}
        fetcher={fetcher}
        TableComponent={BoardListTable}
        CardComponent={(props) => (
          <BoardListCard {...props} variant="row" isMobile={isMobile} />
        )}
        initialSort={{ field: 'no', direction: 'desc' }}
        initialPage={1}
        initialPageSize={10}
        toolbarLeft={
          <CoursesToolbar
            isMobile={isMobile}
            status={statusFilter}
            lecture={lectureFilter}
            course={courseFilter} // Pass new course filter state
            onStatusChange={handleStatusChange}
            onLectureChange={handleLectureChange}
            onCourseChange={handleCourseChange} // Pass new course filter handler
            statusComboData={statusComboData}
            lectureComboData={lectureComboData}
          />
        }
        onSearchSubmit={(searchValue) => {
          setConfirmedStatus(statusFilter)
          setConfirmedLecture(lectureFilter)
          setConfirmedCourse(courseFilter) // Set confirmed course filter
          setConfirmedSearch(searchValue)
        }}
      />
      <CancelDialog open={dialogOpen} onClose={handleCloseDialog} onSubmit={submitDialogCancelProc} data={selectedRow} />
    </>
  )
}
